/**
 * 8D Module API - Team Members
 *
 * Database operations for 8D team formation (D1).
 * Uses mod_8d schema for module isolation.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface TeamMember {
  id: string;
  tenant_id: string;
  claim_id: string;
  name: string;
  role: string;
  email: string | null;
  created_at: string;
}

export interface CreateTeamMemberInput {
  tenant_id: string;
  claim_id: string;
  name: string;
  role: string;
  email?: string | null;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches all team members for a claim.
 */
export async function fetchTeamMembersByClaim(claimId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('team_members')
    .select('*')
    .eq('claim_id', claimId)
    .order('created_at');

  if (error) throw error;
  return data;
}

/**
 * Gets the tenant_id for a claim (helper for components without tenant context).
 */
export async function getClaimTenantId(claimId: string): Promise<string | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select('tenant_id')
    .eq('id', claimId)
    .single();

  if (error) return null;
  return data?.tenant_id ?? null;
}

/**
 * Creates a new team member.
 */
export async function createTeamMember(input: CreateTeamMemberInput): Promise<TeamMember> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('team_members')
    .insert({
      tenant_id: input.tenant_id,
      claim_id: input.claim_id,
      name: input.name,
      role: input.role,
      email: input.email || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Creates a team member for a claim, auto-fetching tenant_id if not provided.
 * Useful for supplier portal where tenant context may not be available.
 */
export async function createTeamMemberForClaim(
  claimId: string,
  member: { name: string; role: string; email?: string | null }
): Promise<TeamMember> {
  // First get the tenant_id from the claim
  const tenantId = await getClaimTenantId(claimId);
  if (!tenantId) {
    throw new Error('Claim not found');
  }

  return createTeamMember({
    tenant_id: tenantId,
    claim_id: claimId,
    name: member.name,
    role: member.role,
    email: member.email,
  });
}

/**
 * Deletes a team member.
 */
export async function deleteTeamMember(memberId: string): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('team_members')
    .delete()
    .eq('id', memberId);

  if (error) throw error;
}
